// One member, over the list (spec/07 §6, F-ORG-13, F-IAM-10): who they are, the role they hold, and
// the actions on them — change their role, suspend or reinstate them, reset their PIN at this venue,
// or remove them. Each is behind its own permission and checked again by the server, which also holds
// the invariants a console cannot see (the last active owner, a role above the actor's own).
// Their own comp limit, in place of their role's (spec/07 §6.4; owner decision 2026-09-30), is set
// here too — an owner carries none, so an owner's dialog does not show it.

import type { Member } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Avatar, Badge, Button, Dialog, Select, Skeleton, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { Icon } from '../../shell/nav-icons';
import { useScopeIds } from '../../shell/scope';
import { Facts, Notice, Section } from '../../ui';
import { CompLimitEditor, type CompValues } from './CompLimitEditor';
import { useVenueNames } from './StaffPage';
import {
  memberName,
  useChangeMemberLimits,
  useChangeMemberRole,
  useChangeMemberStatus,
  useMemberLimits,
  useRemoveMember,
  useResetStaffPin,
  useRoles,
} from './queries';
import { useProblem } from '../../api/use-problem';

const MEMBERS_ROLES = 'members:roles';
const MEMBERS_REMOVE = 'members:remove';
const MEMBERS_UPDATE = 'members:update';
const OWNER_ROLE = 'owner';

export function MemberDialog({ member, onClose }: { readonly member: Member | null; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog
      open={member !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="md"
      eyebrow={t('staff.member.eyebrow')}
      title={member === null ? t('staff.member.title') : memberName(member)}
    >
      {member !== null ? <MemberDetail member={member} onClose={onClose} /> : null}
    </Dialog>
  );
}

function MemberDetail({ member, onClose }: { readonly member: Member; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const toast = useToast();
  const venueNames = useVenueNames();

  const roles = useRoles(scope.organizationId);
  const changeRole = useChangeMemberRole(scope.organizationId);
  const changeStatus = useChangeMemberStatus(scope.organizationId);
  const remove = useRemoveMember(scope.organizationId);
  const resetPin = useResetStaffPin(scope.organizationId, scope.venueId);
  // Suspending locks a member out at once, and removing ends their access: each confirms in place first.
  const [confirm, setConfirm] = useState<'suspend' | 'remove' | null>(null);

  const canRoles = access.status === 'ready' && access.can(MEMBERS_ROLES);
  const canRemove = access.status === 'ready' && access.can(MEMBERS_REMOVE);
  const canUpdate = access.status === 'ready' && access.can(MEMBERS_UPDATE);

  const ok = (message: string) => toast.show({ title: message, tone: 'ok' });
  const fail = (message: string) => toast.show({ title: message, tone: 'danger' });

  return (
    <div className="console-people-detail">
      <header className="console-people-head">
        <Avatar name={memberName(member)} size="lg" decorative />
        {/* The dialog's title is the member's name; beside the avatar is their state. */}
        <div className="console-people-head__who">
          {member.status === 'suspended' ? (
            <Badge tone="warn" dot>
              {t('staff.members.suspended')}
            </Badge>
          ) : (
            <Badge tone="ok" dot>
              {t('staff.members.active')}
            </Badge>
          )}
        </div>
      </header>

      <Section title={t('staff.member.facts')}>
        <Facts
          items={[
            { key: 'email', label: t('staff.member.email'), value: member.email ?? <span className="console-subtle">{t('players.notGiven')}</span> },
            { key: 'phone', label: t('staff.member.phone'), value: member.phoneNumber ?? <span className="console-subtle">{t('players.notGiven')}</span> },
            { key: 'venues', label: t('staff.member.venues'), value: venueNames(member.venueIds) },
            { key: 'joined', label: t('staff.member.joined'), value: format.date(member.joinedAt, 'long') },
            { key: 'twoFactor', label: t('staff.member.twoFactor'), value: member.secondFactorEnabled ? t('staff.members.twoFactorOn') : t('staff.members.twoFactorOff') },
          ]}
        />
      </Section>

      <Section title={t('staff.member.role')} description={t('staff.member.roleHint')}>
        {canRoles ? (
          // The section's title says what this is; the select is named for a screen reader rather than labelled again.
          <Select
            aria-label={t('staff.member.roleLabel')}
            value={member.role}
            disabled={changeRole.isPending || roles.data === undefined}
            options={(roles.data ?? []).map((role) => ({ value: role.name, label: role.name }))}
            onValueChange={(role) => {
              if (role === member.role) return;
              changeRole.mutate({ memberId: member.id, role }, { onSuccess: () => ok(t('staff.member.roleChanged', { role })), onError: (failure) => fail(problem.text(failure, { fallback: t('staff.member.roleFailed') })) });
            }}
          />
        ) : (
          <p>
            <Badge tone="neutral">{member.role}</Badge>
          </p>
        )}
      </Section>

      {member.role !== OWNER_ROLE ? (
        <MemberCompLimit member={member} canEdit={canRoles && access.memberId !== member.id} canGiveUnlimited={access.role === OWNER_ROLE} />
      ) : null}

      {canRemove || (canUpdate && scope.venueId !== undefined) ? (
        <Section title={t('staff.member.manage')}>
          <div className="console-form__actions">
            {canRemove ? (
              member.status === 'suspended' ? (
                <Button
                  variant="secondary"
                  busy={changeStatus.isPending}
                  onClick={() => changeStatus.mutate({ memberId: member.id, status: 'active' }, { onSuccess: () => ok(t('staff.member.reinstated')), onError: (failure) => fail(problem.text(failure, { fallback: t('staff.member.statusFailed') })) })}
                >
                  {t('staff.member.reinstate')}
                </Button>
              ) : (
                <Button variant="secondary" disabled={confirm === 'suspend'} onClick={() => setConfirm('suspend')}>
                  {t('staff.member.suspend')}
                </Button>
              )
            ) : null}

            {canUpdate && scope.venueId !== undefined ? (
              <Button
                variant="ghost"
                icon={<Icon name="pin" />}
                busy={resetPin.isPending}
                onClick={() => resetPin.mutate(member.id, { onSuccess: () => ok(t('staff.member.pinReset')), onError: (failure) => fail(problem.text(failure, { fallback: t('staff.member.pinFailed') })) })}
              >
                {t('staff.member.resetPin')}
              </Button>
            ) : null}
          </div>

          {confirm === 'suspend' ? (
            <Notice tone="warn" role="alert" title={t('staff.member.suspendConfirmTitle')}>
              <p>{t('staff.member.suspendConfirmBody', { name: memberName(member) })}</p>
              <div className="console-form__actions">
                <Button variant="ghost" onClick={() => setConfirm(null)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  busy={changeStatus.isPending}
                  onClick={() =>
                    changeStatus.mutate(
                      { memberId: member.id, status: 'suspended' },
                      {
                        onSuccess: () => {
                          ok(t('staff.member.suspended'));
                          setConfirm(null);
                        },
                        onError: (failure) => fail(problem.text(failure, { fallback: t('staff.member.statusFailed') })),
                      },
                    )
                  }
                >
                  {t('staff.member.suspendConfirm')}
                </Button>
              </div>
            </Notice>
          ) : null}

          {canRemove ? (
            confirm === 'remove' ? (
              <Notice tone="danger" role="alert" title={t('staff.member.removeConfirmTitle')}>
                <p>{t('staff.member.removeConfirmBody', { name: memberName(member) })}</p>
                <div className="console-form__actions">
                  <Button variant="ghost" onClick={() => setConfirm(null)}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="danger"
                    busy={remove.isPending}
                    onClick={() =>
                      remove.mutate(member.id, {
                        onSuccess: () => {
                          ok(t('staff.member.removed', { name: memberName(member) }));
                          onClose();
                        },
                        onError: (failure) => fail(problem.text(failure, { fallback: t('staff.member.removeFailed') })),
                      })
                    }
                  >
                    {t('staff.member.removeConfirm')}
                  </Button>
                </div>
              </Notice>
            ) : (
              <Button variant="danger" icon={<Icon name="close" />} disabled={confirm === 'suspend'} onClick={() => setConfirm('remove')}>
                {t('staff.member.remove')}
              </Button>
            )
          ) : null}
        </Section>
      ) : null}
    </div>
  );
}

/**
 * The member's own comp limit (spec/07 §6.4): follow their role — the default, and what "Follow role"
 * saves back to — or the venue's setting, custom minutes, or no limit. The server holds the full rule
 * (nothing above the actor's own; "no limit" only from an owner or someone whose own comp has none).
 */
function MemberCompLimit({
  member,
  canEdit,
  canGiveUnlimited,
}: {
  readonly member: Member;
  readonly canEdit: boolean;
  readonly canGiveUnlimited: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const scope = useScopeIds();
  const toast = useToast();
  const limits = useMemberLimits(scope.organizationId, member.id);
  const change = useChangeMemberLimits(scope.organizationId, member.id);

  async function save(comp: CompValues | null): Promise<boolean> {
    try {
      await change.mutateAsync({ comp });
    } catch (error) {
      toast.show({ title: problem.text(error, { fallback: t('staff.comp.failed') }), tone: 'danger' });
      return false;
    }
    toast.show({ title: t('staff.comp.saved'), tone: 'ok' });
    return true;
  }

  return (
    <Section title={t('staff.comp.title')} description={t('staff.comp.memberHint')}>
      {limits.data === undefined ? (
        limits.isError ? (
          <p className="console-subtle">{t('staff.comp.failed')}</p>
        ) : (
          <Skeleton shape="text" lines={2} />
        )
      ) : (
        <CompLimitEditor
          value={limits.data.comp}
          forMember
          roleName={member.role}
          canEdit={canEdit}
          canGiveUnlimited={canGiveUnlimited}
          busy={change.isPending}
          onSave={save}
        />
      )}
    </Section>
  );
}
